import {FC, useEffect, useMemo} from 'react';

import * as yup from 'yup';
import {useFormik} from 'formik';
import {mapServerError} from 'core';
import {ServerErrors} from 'components';
import {useCancelPrsesMutation, useGetCancellationReasonsQuery} from "store/post-results-services/postResultsServicesApi";

import {Field, TextArea} from '@britishcouncil/react-solas-ors3';
import {useFormFields, useServerErrors} from '@britishcouncil/react-forms';
import {validationRules} from '@britishcouncil/react-forms/';
import {Select, Button, Modal} from "@britishcouncil/react-extro-ui";

import * as S from './PrsCancelModal.style';
import {RequiredFieldContainer} from "./PrsCancelModal.style";

export const PrsCancelModal: FC<{ onExit: () => void, onSubmitted: () => void, prsesIds: string[], candidateId: string }> = ({onExit, onSubmitted, prsesIds, candidateId}) => {
    const {data: cancellationReasons, isFetching: cancellationReasonsLoading} = useGetCancellationReasonsQuery();
    const [cancelPrses, {isLoading: submitting, error, isSuccess: submitted, isError: isSubmitError}] =
        useCancelPrsesMutation();

    const cancellationReasonsOptions = useMemo(
        () => cancellationReasons?.map(r => ({value: r.id, label: r.name})) ?? [],
        [cancellationReasons]
    );

    const serverErrors = useMemo(() => (isSubmitError ? mapServerError(error) : []), [error, isSubmitError]);

    const formik = useFormik({
        initialValues: {
            cancellationReasonId: '',
            cancellationInfo: '',
        },
        validationSchema: yup.object().shape({
            cancellationReasonId: yup.string().required(validationRules.requiredMessage),
            cancellationInfo: yup.string().optional().max(100, 'Max length is 100'),
        }),
        onSubmit: (values) => {
            cancelPrses({candidateId, prsesIds, ...values})
        },
    });

    const fields = useFormFields(formik);
    const mainErrors = useServerErrors(formik, serverErrors);

    useEffect(() => {
        if (submitted) {
            onSubmitted();
            onExit();
        }
    }, [onExit, onSubmitted, submitted]);

    return (
        <Modal
            ModalActions={
                <S.ModalFooter>
                    <Button onClick={onExit} variant='discreet' disabled={submitting}>Back</Button>
                    <Button onClick={formik.submitForm} disabled={submitting || !formik.isValid}>Save</Button>
                </S.ModalFooter>
            }
            isOpen={true}
            title="Post results services cancellation"
            onClose={onExit}
        >
            <form>
                <S.RequiredFieldContainer>
                    <Field {...fields.cancellationReasonId}
                           label="Cancellation reason"
                           showColon={false}>
                        <Select
                            isMulti={false}
                            options={cancellationReasonsOptions}
                            onBlur={formik.handleBlur}
                            onChange={(selectedOption) => {
                                formik.setFieldValue('cancellationReasonId',
                                    selectedOption ? selectedOption.value : '');
                            }}
                        />
                    </Field>
                </S.RequiredFieldContainer>

                <TextArea {...fields.cancellationInfo}
                          label="Cancellation info"
                          showColon={false}
                          placeholder="Type here"/>

                {!!mainErrors && <ServerErrors errors={mainErrors}/>}
            </form>
        </Modal>
    );
};
